import { supabase } from '../lib/supabase';

// =====================================================
// 환불 관련 타입 정의
// =====================================================

export interface Refund {
  id: string;
  application_id: string;
  refund_status: 'requested' | 'approved' | 'completed' | 'cancelled';
  refund_amount: number;
  refund_reason: string;
  refund_bank?: string;
  refund_account?: string;
  refund_account_holder?: string;
  requested_at: string;
  approved_at?: string;
  completed_at?: string;
  cancelled_at?: string;
  processed_by?: string;
  admin_note?: string;
  rejection_reason?: string;
  privacy_consent?: boolean | null;
  created_at: string;
  updated_at: string;
}

export interface RefundWithApplication extends Refund {
  application?: {
    id: string;
    application_number: string;
    status: string;
    payment_status: string;
    total_amount: number;
    user_id: string;
    class_id: string;
    applicant_name: string;
    applicant_phone: string;
  };
  class_info?: {
    id: string;
    name: string;
    program_id: string;
  };
  program_info?: {
    id: string;
    name: string;
  };
  user_info?: {
    name: string;
    phone: string;
    email: string;
    birthdate: string;
    guardian_name: string;
    guardian_phone: string;
    grade: string;
    school: string;
  };
}

export interface RequestRefundRequest {
  application_id: string;
  refund_reason: string;
  refund_bank?: string;
  refund_account?: string;
  refund_account_holder?: string;
  privacy_consent?: boolean;
}

export interface RefundResponse {
  success: boolean;
  data?: Refund;
  error?: {
    code: string;
    message: string;
    details?: string;
  };
}

export interface RefundListResponse {
  success: boolean;
  data?: RefundWithApplication[];
  error?: {
    code: string;
    message: string;
    details?: string;
  };
}

// =====================================================
// 수강생용 환불 서비스
// =====================================================

/**
 * 환불 신청
 * - status='registered', payment_status='completed'인 신청만 가능
 * - 이미 환불 신청이 있으면 불가
 */
export const requestRefund = async (
  request: RequestRefundRequest
): Promise<RefundResponse> => {
  try {
    console.log('Request refund:', request);

    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return {
        success: false,
        error: {
          code: 'UNAUTHORIZED',
          message: '로그인이 필요합니다.',
        },
      };
    }

    const { data, error } = await supabase.rpc('request_refund', {
      p_application_id: request.application_id,
      p_refund_reason: request.refund_reason,
      p_refund_bank: request.refund_bank || null,
      p_refund_account: request.refund_account || null,
      p_refund_account_holder: request.refund_account_holder || null,
      p_privacy_consent: request.privacy_consent ?? null,
    });

    if (error) {
      console.error('Request refund RPC error:', error);
      return {
        success: false,
        error: {
          code: 'RPC_ERROR',
          message: error.message,
        },
      };
    }

    const result = data?.[0];
    if (!result?.success) {
      return {
        success: false,
        error: {
          code: result?.error_code || 'UNKNOWN_ERROR',
          message: result?.error_message || '환불 신청 중 오류가 발생했습니다.',
        },
      };
    }

    return {
      success: true,
      data: {
        id: result.refund_id,
        application_id: request.application_id,
        refund_status: 'requested',
        refund_amount: result.refund_amount,
        refund_reason: request.refund_reason,
        refund_bank: request.refund_bank,
        refund_account: request.refund_account,
        refund_account_holder: request.refund_account_holder,
        requested_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
    };
  } catch (error: any) {
    console.error('Request refund error:', error);
    return {
      success: false,
      error: {
        code: 'UNEXPECTED_ERROR',
        message: error.message,
      },
    };
  }
};

/**
 * 환불 신청 취소 (수강생)
 * - refund_status='requested'인 경우만 가능
 */
export const cancelRefundRequest = async (
  refundId: string
): Promise<RefundResponse> => {
  try {
    console.log('Cancel refund request:', refundId);

    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return {
        success: false,
        error: {
          code: 'UNAUTHORIZED',
          message: '로그인이 필요합니다.',
        },
      };
    }

    const { data, error } = await supabase.rpc('cancel_refund_request', {
      p_refund_id: refundId,
    });

    if (error) {
      console.error('Cancel refund request RPC error:', error);
      return {
        success: false,
        error: {
          code: 'RPC_ERROR',
          message: error.message,
        },
      };
    }

    const result = data?.[0];
    if (!result?.success) {
      return {
        success: false,
        error: {
          code: result?.error_code || 'UNKNOWN_ERROR',
          message: result?.error_message || '환불 취소 중 오류가 발생했습니다.',
        },
      };
    }

    return { success: true };
  } catch (error: any) {
    console.error('Cancel refund request error:', error);
    return {
      success: false,
      error: {
        code: 'UNEXPECTED_ERROR',
        message: error.message,
      },
    };
  }
};

/**
 * 특정 신청의 환불 정보 조회
 */
export const getRefundByApplication = async (
  applicationId: string
): Promise<RefundResponse> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return {
        success: false,
        error: {
          code: 'UNAUTHORIZED',
          message: '로그인이 필요합니다.',
        },
      };
    }

    const { data, error } = await supabase.rpc('get_refund_by_application', {
      p_application_id: applicationId,
    });

    if (error) {
      console.error('Get refund RPC error:', error);
      return {
        success: false,
        error: {
          code: 'RPC_ERROR',
          message: error.message,
        },
      };
    }

    const result = data?.[0];
    if (!result?.success) {
      // 환불 없음은 에러가 아닌 정상 케이스
      if (result?.error_code === 'NO_REFUND') {
        return { success: true, data: undefined };
      }
      return {
        success: false,
        error: {
          code: result?.error_code || 'UNKNOWN_ERROR',
          message: result?.error_message || '환불 조회 중 오류가 발생했습니다.',
        },
      };
    }

    return {
      success: true,
      data: {
        id: result.refund_id,
        application_id: applicationId,
        refund_status: result.refund_status,
        refund_amount: result.refund_amount,
        refund_reason: result.refund_reason,
        refund_bank: result.refund_bank,
        refund_account: result.refund_account,
        refund_account_holder: result.refund_account_holder,
        requested_at: result.requested_at,
        approved_at: result.approved_at,
        completed_at: result.completed_at,
        cancelled_at: result.cancelled_at,
        admin_note: result.admin_note,
        rejection_reason: result.rejection_reason,
        created_at: result.requested_at,
        updated_at: result.requested_at,
      },
    };
  } catch (error: any) {
    console.error('Get refund error:', error);
    return {
      success: false,
      error: {
        code: 'UNEXPECTED_ERROR',
        message: error.message,
      },
    };
  }
};

// =====================================================
// 관리자용 환불 서비스
// =====================================================

/**
 * 관리자: 환불 목록 조회
 */
export const adminGetRefunds = async (
  statusFilter?: 'requested' | 'approved' | 'completed' | 'cancelled' | 'all'
): Promise<RefundListResponse> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return {
        success: false,
        error: {
          code: 'UNAUTHORIZED',
          message: '로그인이 필요합니다.',
        },
      };
    }

    let query = supabase
      .from('refunds')
      .select(`
        *,
        applications!inner (
          id,
          application_number,
          status,
          payment_status,
          payment_amount,
          registrant_id,
          student_id,
          class_id,
          student_name,
          program_classes (
            id,
            class_name,
            program_id,
            programs (
              id,
              name
            )
          )
        )
      `)
      .order('requested_at', { ascending: false });

    if (statusFilter && statusFilter !== 'all') {
      query = query.eq('refund_status', statusFilter);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Admin get refunds error:', error);
      return {
        success: false,
        error: {
          code: 'QUERY_ERROR',
          message: error.message,
        },
      };
    }

    // profiles 별도 조인
    const userIds = Array.from(new Set(
      (data || []).map((item: any) => item.applications?.student_id || item.applications?.registrant_id).filter(Boolean)
    ));

    let profileMap = new Map<string, any>();
    if (userIds.length > 0) {
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, name, phone, email, birthdate, guardian_name, guardian_phone, grade, school')
        .in('id', userIds);
      profileMap = new Map((profiles || []).map(p => [p.id, p]));
    }

    // 데이터 변환
    const refunds: RefundWithApplication[] = (data || []).map((item: any) => {
      const profile = profileMap.get(item.applications?.student_id || item.applications?.registrant_id);
      return {
      ...item,
      application: item.applications ? {
        id: item.applications.id,
        application_number: item.applications.application_number,
        status: item.applications.status,
        payment_status: item.applications.payment_status,
        total_amount: item.applications.payment_amount,
        user_id: item.applications.student_id || item.applications.registrant_id,
        class_id: item.applications.class_id,
        applicant_name: item.applications.student_name,
        applicant_phone: profile?.phone || null,
      } : undefined,
      class_info: item.applications?.program_classes ? {
        id: item.applications.program_classes.id,
        name: item.applications.program_classes.class_name,
        program_id: item.applications.program_classes.program_id,
      } : undefined,
      program_info: item.applications?.program_classes?.programs ? {
        id: item.applications.program_classes.programs.id,
        name: item.applications.program_classes.programs.name,
      } : undefined,
      user_info: {
        name: profile?.name || item.applications?.student_name,
        phone: profile?.phone || null,
        email: profile?.email || null,
        birthdate: profile?.birthdate || null,
        guardian_name: profile?.guardian_name || null,
        guardian_phone: profile?.guardian_phone || null,
        grade: profile?.grade || null,
        school: profile?.school || null,
      },
      applications: undefined, // 중첩 제거
    };
    });

    return { success: true, data: refunds };
  } catch (error: any) {
    console.error('Admin get refunds error:', error);
    return {
      success: false,
      error: {
        code: 'UNEXPECTED_ERROR',
        message: error.message,
      },
    };
  }
};

/**
 * 관리자: 환불 승인
 * - requested → approved
 */
export const adminApproveRefund = async (
  refundId: string,
  adminNote?: string
): Promise<RefundResponse> => {
  try {
    console.log('Admin approve refund:', refundId);

    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return {
        success: false,
        error: {
          code: 'UNAUTHORIZED',
          message: '로그인이 필요합니다.',
        },
      };
    }

    const { data, error } = await supabase.rpc('admin_approve_refund', {
      p_refund_id: refundId,
      p_admin_id: user.id,
      p_admin_note: adminNote || null,
    });

    if (error) {
      console.error('Admin approve refund RPC error:', error);
      return {
        success: false,
        error: {
          code: 'RPC_ERROR',
          message: error.message,
        },
      };
    }

    const result = data?.[0];
    if (!result?.success) {
      return {
        success: false,
        error: {
          code: result?.error_code || 'UNKNOWN_ERROR',
          message: result?.error_message || '환불 승인 중 오류가 발생했습니다.',
        },
      };
    }

    return { success: true };
  } catch (error: any) {
    console.error('Admin approve refund error:', error);
    return {
      success: false,
      error: {
        code: 'UNEXPECTED_ERROR',
        message: error.message,
      },
    };
  }
};

/**
 * 관리자: 환불 거절
 * - requested → cancelled
 */
export const adminRejectRefund = async (
  refundId: string,
  rejectionReason: string
): Promise<RefundResponse> => {
  try {
    console.log('Admin reject refund:', refundId);

    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return {
        success: false,
        error: {
          code: 'UNAUTHORIZED',
          message: '로그인이 필요합니다.',
        },
      };
    }

    const { data, error } = await supabase.rpc('admin_reject_refund', {
      p_refund_id: refundId,
      p_admin_id: user.id,
      p_rejection_reason: rejectionReason,
    });

    if (error) {
      console.error('Admin reject refund RPC error:', error);
      return {
        success: false,
        error: {
          code: 'RPC_ERROR',
          message: error.message,
        },
      };
    }

    const result = data?.[0];
    if (!result?.success) {
      return {
        success: false,
        error: {
          code: result?.error_code || 'UNKNOWN_ERROR',
          message: result?.error_message || '환불 거절 중 오류가 발생했습니다.',
        },
      };
    }

    return { success: true };
  } catch (error: any) {
    console.error('Admin reject refund error:', error);
    return {
      success: false,
      error: {
        code: 'UNEXPECTED_ERROR',
        message: error.message,
      },
    };
  }
};

/**
 * 관리자: 환불 완료 처리
 * - approved → completed
 * - application.payment_status → 'refunded'
 * - application.status → 'cancelled'
 * - 대기자 자동 승급 처리
 */
export const adminCompleteRefund = async (
  refundId: string,
  adminNote?: string
): Promise<RefundResponse> => {
  try {
    console.log('Admin complete refund:', refundId);

    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return {
        success: false,
        error: {
          code: 'UNAUTHORIZED',
          message: '로그인이 필요합니다.',
        },
      };
    }

    const { data, error } = await supabase.rpc('admin_complete_refund', {
      p_refund_id: refundId,
      p_admin_id: user.id,
      p_admin_note: adminNote || null,
    });

    if (error) {
      console.error('Admin complete refund RPC error:', error);
      return {
        success: false,
        error: {
          code: 'RPC_ERROR',
          message: error.message,
        },
      };
    }

    const result = data?.[0];
    if (!result?.success) {
      return {
        success: false,
        error: {
          code: result?.error_code || 'UNKNOWN_ERROR',
          message: result?.error_message || '환불 완료 처리 중 오류가 발생했습니다.',
        },
      };
    }

    return { success: true };
  } catch (error: any) {
    console.error('Admin complete refund error:', error);
    return {
      success: false,
      error: {
        code: 'UNEXPECTED_ERROR',
        message: error.message,
      },
    };
  }
};

/**
 * 관리자: 직접 환불 처리 (바로 완료)
 * - 수강생의 환불 신청 없이 관리자가 직접 환불 처리
 * - refund 레코드를 completed 상태로 즉시 생성
 * - application: status → cancelled, payment_status → refunded
 */
export const adminInitiateRefund = async (
  applicationId: string,
  refundReason: string,
  adminNote?: string
): Promise<RefundResponse> => {
  try {
    console.log('Admin initiate refund:', applicationId);

    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return {
        success: false,
        error: {
          code: 'UNAUTHORIZED',
          message: '로그인이 필요합니다.',
        },
      };
    }

    const { data, error } = await supabase.rpc('admin_initiate_refund', {
      p_application_id: applicationId,
      p_admin_id: user.id,
      p_refund_reason: refundReason,
      p_admin_note: adminNote || null,
    });

    if (error) {
      console.error('Admin initiate refund RPC error:', error);
      return {
        success: false,
        error: {
          code: 'RPC_ERROR',
          message: error.message,
        },
      };
    }

    const result = data?.[0];
    if (!result?.success) {
      return {
        success: false,
        error: {
          code: result?.error_code || 'UNKNOWN_ERROR',
          message: result?.error_message || '환불 처리 중 오류가 발생했습니다.',
        },
      };
    }

    return { success: true };
  } catch (error: any) {
    console.error('Admin initiate refund error:', error);
    return {
      success: false,
      error: {
        code: 'UNEXPECTED_ERROR',
        message: error.message,
      },
    };
  }
};

// =====================================================
// 환불 상태 라벨 헬퍼
// =====================================================

export const getRefundStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    requested: '승인 대기중',
    approved: '환불 처리중',
    completed: '환불 완료',
    cancelled: '환불 거절',
  };
  return labels[status] || status;
};

export const getRefundStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    requested: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-gray-100 text-gray-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};
